// Reads `fn_get_aim_provider_coverage`: per provider, whether each drawer section is empty
// because nothing applies, nothing was found, or collection failed — so the drawer can say which.

import { NO_DATA_MESSAGE } from './dataStatus';

/**
 * Find a provider's coverage row.
 *
 * Matches `provider_id` first, then `display_name`: a model change gives a provider path a new id
 * between runs, so an id carried in a URL can miss while the display name still resolves.
 *
 * @param {Array<{provider_id?: string, display_name?: string}>|null|undefined} coverage coverage rows
 * @param {string} providerId id from the URL or scorecard row
 * @param {string} displayName display name from the URL or scorecard row
 * @returns {object|null}
 */
export function coverageFor(coverage, providerId, displayName) {
  if (!Array.isArray(coverage)) return null;
  const byId = providerId && coverage.find((c) => c.provider_id === providerId);
  if (byId) return byId;
  const byName = displayName && coverage.find((c) => c.display_name === displayName);
  return byName || null;
}

/**
 * Explain an empty drawer section from its coverage status.
 *
 * `ok` and any status this build doesn't know return null: the caller keeps its generic empty
 * text rather than invent a reason.
 *
 * @param {'categories'|'facts'|'citations'} section
 * @param {string|undefined} status the section's coverage status
 * @param {{search_enabled?: boolean}|null|undefined} entry the coverage row (read for `search_enabled`)
 * @returns {string|null}
 */
export function sectionEmptyMessage(section, status, entry) {
  switch (status) {
    case 'collection_failed':
      return NO_DATA_MESSAGE;
    case 'none_found':
      return 'None in this run';
    case 'not_applicable':
      if (section !== 'citations') return "Search results aren't graded per fact";
      return entry?.search_enabled
        ? 'Organic results are a ranked list of links, not cited sources'
        : "This mode doesn't search the web, so it cites no sources";
    default:
      return null;
  }
}

/**
 * Did collection fail for every drawer section of this provider?
 *
 * @param {{categories?: {status?: string}, facts?: {status?: string}, citations?: {status?: string}}|null|undefined} entry
 * @returns {boolean}
 */
export const allSectionsFailed = (entry) => Boolean(entry)
  && ['categories', 'facts', 'citations'].every((s) => entry[s]?.status === 'collection_failed');
